import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Mail, Lock, Phone, User, ArrowLeft, Loader2, Home, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSignupPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Form, 2: Verify OTP

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [communityName, setCommunityName] = useState('');
  const [communityAddress, setCommunityAddress] = useState('');
  const [totalUnits, setTotalUnits] = useState('');

  // OTP verification
  const [otpCode, setOtpCode] = useState('');
  const [mockOtp, setMockOtp] = useState('');

  // Redirect if already logged in
  if (user) {
    if (user.role === 'super_admin') navigate('/superadmin', { replace: true });
    else if (user.role === 'admin') navigate('/admin', { replace: true });
    else navigate('/app', { replace: true });
    return null;
  }

  const handleInitiateSignup = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!totalUnits || parseInt(totalUnits) < 1) {
      toast.error('Total units must be at least 1');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/admin/signup/initiate', {
        email,
        name,
        phone,
        password,
        community_name: communityName,
        community_address: communityAddress,
        total_units: parseInt(totalUnits)
      });
      
      if (res.data.mock_otp) {
        setMockOtp(res.data.mock_otp);
      }
      toast.success('Verification code sent to your email!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate signup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndComplete = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Enter 6-digit verification code');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/admin/signup/verify', {
        email,
        otp_code: otpCode
      });
      
      login(res.data.token, res.data.user);
      toast.success(`Welcome! Your community "${res.data.community.name}" has been created.`);
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="admin-signup-page">
      {/* Left Panel - Decorative */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-slate-900 items-center justify-center p-12">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/30 to-slate-900" />
        <div className="relative text-center">
          <div className="w-16 h-16 rounded-2xl bg-emerald-600 flex items-center justify-center mx-auto mb-8">
            <Building2 size={32} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-2">FreeCoffee</h2>
          <p className="text-emerald-400 text-sm font-medium mb-4">Your Free CommunityOS</p>
          <p className="text-slate-400 max-w-sm mx-auto leading-relaxed">
            Register as a Community Admin and start managing your apartment complex, society, or gated community.
          </p>
          <div className="mt-12 space-y-3 max-w-xs mx-auto text-left">
            {[
              'Create and manage units',
              'Generate maintenance bills',
              'Track payments & dues',
              'Post announcements',
              'Handle resident tickets'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50 overflow-y-auto">
        <div className="w-full max-w-[450px] py-8">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
            <ArrowLeft size={14} /> Back to login
          </Link>

          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-1">
            Register as Community Admin
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            {step === 1 ? 'Create your account and community' : 'Verify your email to complete signup'}
          </p>

          {step === 1 ? (
            <form onSubmit={handleInitiateSignup} className="space-y-4">
              {/* Personal Details */}
              <Card className="border-slate-100">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2"><User size={14} className="text-emerald-600" />Personal Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">Full Name *</Label>
                    <Input
                      value={name} onChange={e => setName(e.target.value)}
                      placeholder="John Doe" required
                      data-testid="signup-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">Email *</Label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        type="email" value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com" className="pl-9" required
                        data-testid="signup-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">Phone (optional)</Label>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={phone} onChange={e => setPhone(e.target.value)}
                        placeholder="+91 98765 43210" className="pl-9"
                        data-testid="signup-phone-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-600 mb-1.5 block">Password *</Label>
                      <div className="relative">
                        <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          type="password" value={password} onChange={e => setPassword(e.target.value)}
                          placeholder="Min 6 chars" className="pl-9" required
                          data-testid="signup-password-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 mb-1.5 block">Confirm Password *</Label>
                      <Input
                        type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                        placeholder="Confirm" required
                        data-testid="signup-confirm-password-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Community Details */}
              <Card className="border-slate-100">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2"><Building2 size={14} className="text-emerald-600" />Community Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">Community Name *</Label>
                    <Input
                      value={communityName} onChange={e => setCommunityName(e.target.value)}
                      placeholder="e.g., Sunrise Apartments, Green Valley Society" required
                      data-testid="signup-community-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">Address (optional)</Label>
                    <Input
                      value={communityAddress} onChange={e => setCommunityAddress(e.target.value)}
                      placeholder="123 Main Street, City"
                      data-testid="signup-community-address-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">Total Units/Flats *</Label>
                    <div className="relative">
                      <Home size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        type="number" min="1" value={totalUnits} onChange={e => setTotalUnits(e.target.value)}
                        placeholder="e.g., 50" className="pl-9" required
                        data-testid="signup-total-units-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11" disabled={loading} data-testid="signup-submit-btn">
                {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Continue to Verify Email
              </Button>

              <p className="text-center text-xs text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-emerald-600 font-medium hover:underline">Sign In</Link>
              </p>
            </form>
          ) : (
            <div className="space-y-4">
              <Card className="border-emerald-100 bg-emerald-50/50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Mail size={18} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Verify your email</p>
                      <p className="text-xs text-slate-500 mt-1">
                        We've sent a 6-digit verification code to <span className="font-medium text-slate-700">{email}</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {mockOtp && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-700 font-medium">Mock OTP (for testing): <span className="font-mono text-base">{mockOtp}</span></p>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Verification Code</Label>
                <Input
                  value={otpCode} onChange={e => setOtpCode(e.target.value)}
                  placeholder="Enter 6-digit code" maxLength={6}
                  className="text-center tracking-widest text-lg h-12"
                  data-testid="signup-otp-input"
                />
              </div>

              <Button onClick={handleVerifyAndComplete} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11" disabled={loading} data-testid="signup-verify-btn">
                {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Verify & Create Account
              </Button>

              <button 
                className="text-xs text-slate-500 hover:text-emerald-600 w-full text-center"
                onClick={() => { setStep(1); setOtpCode(''); setMockOtp(''); }}
              >
                ← Go back and edit details
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
